import MonacoEditor, { EditorWillMount } from 'react-monaco-editor'
import { noop, isString } from 'lodash-es'
import styled from '@emotion/styled'
import React, { useCallback, useRef, useEffect } from 'react'
import { colors, clearSky, watermelon } from '@waylay/react-components'
import extendDeclarativeBinding from '../../../Common/Editor/languages/declarative-binding'
import { HStack } from '~/components/Common/Stack'
import OpenInEditor from '../OpenInEditor'
import { useSafeEditorContainer } from '../EditorContext'
import { IPropertyInput } from '../../types'
import './MonacoEditor.css'

interface IEditorField extends IEditorFieldWrapper {
  editorWillMount?: EditorWillMount
  variables?: any
}

const EditorField = ({
  property,
  value,
  language,
  variables,
  setFieldValue,
  editorWillMount = noop,
  isDeclarativelyBound,
  isError,
}: IEditorField) => {
  const editorRef = useRef<any>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!containerRef.current) return

    const resizeObserver = new ResizeObserver(() => {
      if (editorRef.current) {
        // Debounce and prevent excessive layout calls
        setTimeout(() => {
          editorRef.current?.layout()
        }, 10)
      }
    })

    resizeObserver.observe(containerRef.current)

    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  const handleEditorDidMount = (editor: any) => {
    editorRef.current = editor
    setTimeout(() => {
      editor.layout()
    }, 50)
  }

  return (
    <HStack>
      <EditorFieldWrapper ref={containerRef} isError={isError}>
        <MonacoEditor
          width="100%"
          height="1.45rem"
          language={language}
          value={value}
          onChange={value => setFieldValue(property.name, value)}
          theme={null}
          options={{
            minimap: { enabled: false },
            glyphMargin: false,
            folding: false,
            lineNumbers: 'off',
            lineDecorationsWidth: 0,
            lineNumbersMinChars: 0,
            scrollbar: { vertical: 'hidden', horizontal: 'hidden' },
            guides: { highlightActiveIndentation: false },
            renderLineHighlight: 'none',
            overviewRulerLanes: 0,
            hideCursorInOverviewRuler: true,
            fixedOverflowWidgets: true,
            fontFamily: 'Fira Sans',
            fontSize: 14,
            colorDecorators: false,
          }}
          editorWillMount={editorWillMount}
          editorDidMount={handleEditorDidMount}
        />
      </EditorFieldWrapper>
      <OpenInEditor
        property={property}
        variables={variables}
        value={value}
        setFieldValue={setFieldValue}
        isDeclarativelyBound={isDeclarativelyBound}
      />
    </HStack>
  )
}

const DeclarativeBindingEditorField = (props: IEditorFieldWrapper) => {
  const context = useSafeEditorContainer()
  const declarativeBindingSchema = context?.declarativeBindingSchema

  const editorWillMount = useCallback(
    editor => {
      if (declarativeBindingSchema) {
        extendDeclarativeBinding(editor, declarativeBindingSchema)
      }
    },
    [declarativeBindingSchema],
  )

  return (
    <EditorField
      editorWillMount={editorWillMount}
      {...props}
      variables={props.variables}
    />
  )
}

interface IEditorFieldWrapper extends IPropertyInput {
  language?: string
  variables?: any
}

const EditorFieldContainer = ({
  property,
  value: _value,
  language = 'declarative-binding',
  setFieldValue,
  isDeclarativelyBound,
  variables,
  isError,
}: IEditorFieldWrapper) => {
  const value = isString(_value) ? _value : _value ? JSON.stringify(_value) : '' // editor expects a string
  const valueWithoutLineBreaks = value.replace(/(\r\n|\n|\r)/gm, '  ') // single line EditorField does not allow linebreaks

  if (language === 'declarative-binding') {
    return (
      <DeclarativeBindingEditorField
        property={property}
        value={value}
        language={language}
        isError={isError}
        variables={variables}
        setFieldValue={setFieldValue}
        isDeclarativelyBound={isDeclarativelyBound}
      />
    )
  }

  return (
    <EditorField
      property={property}
      value={valueWithoutLineBreaks}
      language={language}
      setFieldValue={setFieldValue}
      isDeclarativelyBound={isDeclarativelyBound}
      isError={isError}
      variables={variables}
    />
  )
}

const EditorFieldWrapper = styled.div<{ isError?: boolean }>`
  border: solid 1px #e8e8e8;
  border-color: ${({ isError }) =>
    isError ? watermelon : 'rgb(232, 232, 232)'};
  border-style: solid;
  border-width: 1px;
  border-radius: 0.333333rem;
  height: 2.4rem;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 0.85rem;
  flex: 1;
  position: relative;
  overflow: hidden;
  min-width: 0;

  /* Minimal fix to prevent Monaco Editor collapse in table contexts */
  & .monaco-editor {
    min-width: 120px;
  }

  & :focus-within {
    border-color: ${({ isError }) =>
      isError ? watermelon : colors.withWeight(clearSky, 300)} !important;
  }

  & * {
    font-weight: 400 !important;
    caret-color: black;
  }

  & .monaco-editor input {
    color: #59595b !important;
  }

  $ monaco-editor .view-line {
    background-color: white;
  }

  & .monaco-editor .view-line span {
    color: #59595b !important;
  }
`

export default EditorFieldContainer
